import { JSX, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'

import config from '@config'
import {
  getAureusBatchRecommendations,
  GetAureusBatchRecommendationsResponse,
  triggerAureusDeboosting,
} from '@utils/aureus'
import useHasAureusJsClientScriptInitialized from '@hooks/useHasAureusJsClientScriptInitialized'
import usePageMetadata from '@hooks/usePageMetadata'
import { CarouselItem, CarouselWidgetAPIProps } from '@widgets/Carousel'
import { JSONWidgetType as TeaserStandardVerticalJSONWidgetType } from '@widgets/TeaserStandardVertical'
import JSONRenderer from '@components/JSONRenderer'

const {
  backend: { deploymentEnv },
} = config

interface BlickPlusSlidesProps {
  fallbackItems: CarouselWidgetAPIProps['items']
}

interface AureusBlickPlusCarouselItem {
  aureusOfferId: string
  articleID: string
  teasers: {
    standardVertical: TeaserStandardVerticalJSONWidgetType
  }
}

type GetAureusCarouselItems = (
  sectionName: 'home' | 'sport'
) => GetAureusBatchRecommendationsResponse<AureusBlickPlusCarouselItem>

const getAureusCarouselItems: GetAureusCarouselItems = (sectionName) =>
  getAureusBatchRecommendations<AureusBlickPlusCarouselItem>({
    'polaris-plus-content': {
      resultsLimit: 3,
      context: {
        section: `polaris-${sectionName}-plus-content-${deploymentEnv === 'prod' ? 'prod' : 'stg'}`,
        purpose: 'reference',
      },
    },
  } as const)

const getCarouselItems = async (sectionName: 'home' | 'sport') => {
  let responseData: CarouselItem[] = []

  try {
    const queryResult = await getAureusCarouselItems(sectionName)

    responseData =
      queryResult?.results?.['polaris-plus-content']?.recommendations?.reduce(
        (acc, curr) => {
          acc.push({
            ...curr.teasers.standardVertical,
            articleId: curr.articleID,
            aureusOfferId: curr.aureusOfferId,
          } satisfies CarouselItem)

          return acc
        },
        [] as CarouselItem[]
      ) ?? []
  } catch {}

  return responseData
}

const sendDeboostingEvent = (articleId: string, aureusOfferId?: string) => {
  if (aureusOfferId) {
    triggerAureusDeboosting({
      itemsToDeboost: [{ offerId: aureusOfferId, contentId: articleId }],
    })
  }
}

const SwiperSlide = styled.div`
  width: 300px;
`

const BlickPlusSlides = ({
  fallbackItems,
}: BlickPlusSlidesProps): JSX.Element[] => {
  const { contextId } = usePageMetadata()

  const hasAureusJsClientScriptInitialized =
    useHasAureusJsClientScriptInitialized()

  const sectionName = useMemo(
    () => (contextId === '/sport/' ? 'sport' : 'home'),
    [contextId]
  )

  const { data: carouselItems } = useQuery<CarouselItem[]>({
    queryKey: ['blickplus-carousel', sectionName],
    queryFn: () => getCarouselItems(sectionName),
    enabled: hasAureusJsClientScriptInitialized,
    gcTime: 0,
  })

  const itemsToDisplay = useMemo(() => {
    const recommendedItems = carouselItems?.length ? carouselItems : []
    const cookApiItems = fallbackItems.length ? fallbackItems : []
    return [...recommendedItems, ...cookApiItems].slice(0, 3)
  }, [carouselItems, fallbackItems])

  return itemsToDisplay.map((item) => {
    const { articleId, aureusOfferId } = item
    return (
      <SwiperSlide
        key={articleId}
        onClick={() => sendDeboostingEvent(articleId, aureusOfferId)}>
        <JSONRenderer>{item}</JSONRenderer>
      </SwiperSlide>
    )
  })
}

export default BlickPlusSlides
